import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCircleIcon,
  PencilIcon,
  CameraIcon,
  CheckIcon,
  XMarkIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  CalendarIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  IdentificationIcon,
  GlobeAltIcon,
  ClockIcon,
  UserGroupIcon,
  StarIcon,
  TrophyIcon,
  ChartBarIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  EyeIcon,
  ShareIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { UserCircleIcon as UserCircleIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const UserProfile = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    // Simulate fetching user profile data
    const fetchUserProfile = () => {
      const mockProfile = {
        id: 'user123',
        firstName: 'Dr. Sarah',
        lastName: 'Johnson',
        fullName: 'Dr. Sarah Johnson',
        email: 'sarah.johnson@apollohealthcare.com',
        phone: '+1 (555) 123-4567',
        title: 'Senior Cardiologist',
        department: 'Cardiology',
        employeeId: 'AHC-001234',
        location: 'New York, NY',
        timezone: 'Eastern Time (ET)',
        workingHours: { start: '08:00', end: '17:00' },
        avatar: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400',
        coverPhoto: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800',
        bio: 'Experienced cardiologist with over 15 years in cardiovascular medicine. Specialized in interventional cardiology and heart failure management.',
        dateJoined: '2019-03-15',
        lastActive: new Date(),
        status: 'online',
        verified: true,
        
        // Contact Information
        personalInfo: {
          dateOfBirth: '1985-07-12',
          nationality: 'American',
          languages: ['English', 'Spanish', 'French'],
          address: {
            street: '123 Medical Plaza',
            city: 'New York',
            state: 'NY',
            zipCode: '10001',
            country: 'United States'
          },
          emergencyContact: {
            name: 'Michael Johnson',
            relationship: 'Spouse',
            phone: '+1 (555) 987-6543'
          }
        },

        // Professional Information
        professionalInfo: {
          licenseNumber: 'MD-NY-123456',
          specialization: ['Cardiology', 'Interventional Cardiology'],
          education: [
            {
              degree: 'MD',
              institution: 'Harvard Medical School',
              year: '2010',
              field: 'Medicine'
            },
            {
              degree: 'Fellowship',
              institution: 'Mayo Clinic',
              year: '2014',
              field: 'Interventional Cardiology'
            }
          ],
          certifications: [
            {
              name: 'Board Certified in Cardiology',
              issuer: 'American Board of Internal Medicine',
              year: '2013',
              expires: '2028'
            },
            {
              name: 'Advanced Cardiac Life Support (ACLS)',
              issuer: 'American Heart Association',
              year: '2023',
              expires: '2025'
            }
          ],
          publications: 12,
          researchProjects: 3
        },

        // Statistics
        stats: {
          patientsServed: 2847,
          proceduresPerformed: 1254,
          yearsExperience: 15,
          patientSatisfaction: 4.9,
          completedTraining: 24,
          upcomingEvents: 8,
          tasksCompleted: 342,
          documentsShared: 89
        },

        // Achievements
        achievements: [
          {
            id: 1,
            title: 'Excellence in Patient Care',
            description: 'Recognized for outstanding patient satisfaction scores',
            date: '2024-01-15',
            icon: 'trophy',
            category: 'Patient Care'
          },
          {
            id: 2,
            title: 'Research Publication Award',
            description: 'Published breakthrough research in cardiovascular medicine',
            date: '2023-09-20',
            icon: 'academic',
            category: 'Research'
          },
          {
            id: 3,
            title: 'Team Leadership',
            description: 'Successfully led cardiac surgery team for complex procedures',
            date: '2023-06-10',
            icon: 'users',
            category: 'Leadership'
          }
        ],

        // Recent Activity
        recentActivity: [
          {
            id: 1,
            type: 'training',
            title: 'Completed Advanced Cardiac Imaging Course',
            date: new Date(2024, 6, 20),
            icon: 'academic'
          },
          {
            id: 2,
            type: 'document',
            title: 'Updated Patient Care Protocol',
            date: new Date(2024, 6, 18),
            icon: 'document'
          },
          {
            id: 3,
            type: 'event',
            title: 'Attended Medical Conference',
            date: new Date(2024, 6, 15),
            icon: 'calendar'
          },
          {
            id: 4,
            type: 'task',
            title: 'Completed Patient Reviews',
            date: new Date(2024, 6, 12),
            icon: 'check'
          }
        ],

        // Privacy Settings
        privacy: {
          profileVisibility: 'team',
          showContactInfo: true,
          showWorkHistory: true,
          allowMessages: true,
          showOnlineStatus: true
        },

        // Social Links
        socialLinks: {
          linkedin: 'https://linkedin.com/in/sarah-johnson-md',
          twitter: 'https://twitter.com/dr_sarah_j',
          researchgate: 'https://researchgate.net/profile/sarah-johnson'
        }
      };

      setUserProfile(mockProfile);
      setEditForm(mockProfile);
      setLoading(false);
    };

    setTimeout(fetchUserProfile, 500);
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
    setEditForm({ ...userProfile });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({ ...userProfile });
  };

  const handleSave = async () => {
    setSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setUserProfile({ ...editForm });
      setIsEditing(false);
      setSaving(false);
    }, 1000);
  };

  const handleInputChange = (field, value, section = null) => {
    if (section) {
      setEditForm(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setEditForm(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'bg-green-400';
      case 'away':
        return 'bg-yellow-400';
      case 'busy':
        return 'bg-red-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'training':
        return <AcademicCapIcon className="h-4 w-4" />;
      case 'document':
        return <DocumentTextIcon className="h-4 w-4" />;
      case 'event':
        return <CalendarIcon className="h-4 w-4" />;
      case 'task':
        return <CheckIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  const getAchievementIcon = (icon) => {
    switch (icon) {
      case 'trophy':
        return <TrophyIcon className="h-8 w-8 text-yellow-500" />;
      case 'academic':
        return <AcademicCapIcon className="h-8 w-8 text-blue-500" />;
      case 'users':
        return <UserGroupIcon className="h-8 w-8 text-green-500" />;
      default:
        return <StarIcon className="h-8 w-8 text-purple-500" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for User Profile */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <UserCircleIconSolid className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Profile</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Manage your personal information and preferences
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
                <Link
                  to="/profile/settings"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-2" />
                  Settings
                </Link>
                <Link
                  to="/profile/privacy"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-2" />
                  Privacy
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Profile Header Card */}
        <div className="bg-white rounded-lg shadow overflow-hidden mb-6 sm:mb-8">
          {/* Cover Photo */}
          <div className="h-32 sm:h-48 bg-gradient-to-r from-blue-400 to-purple-500 relative">
            <img
              src={userProfile.coverPhoto}
              alt="Cover"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30"></div>
            {isEditing && (
              <button className="absolute top-4 right-4 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70">
                <CameraIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Profile Info */}
          <div className="px-6 py-6">
            <div className="flex flex-col sm:flex-row sm:items-end sm:space-x-6">
              {/* Avatar */}
              <div className="relative -mt-20 mb-4 sm:mb-0">
                <img
                  className="h-32 w-32 rounded-full border-4 border-white shadow-lg"
                  src={userProfile.avatar}
                  alt={userProfile.fullName}
                />
                <div className={`absolute bottom-2 right-2 h-6 w-6 ${getStatusColor(userProfile.status)} rounded-full border-2 border-white`}></div>
                {isEditing && (
                  <button className="absolute bottom-0 right-0 p-2 bg-blue-600 rounded-full text-white shadow-lg hover:bg-blue-700">
                    <CameraIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center">
                      <h2 className="text-2xl font-bold text-gray-900">{userProfile.fullName}</h2>
                      {userProfile.verified && (
                        <CheckIcon className="h-6 w-6 text-blue-500 ml-2" />
                      )}
                    </div>
                    <p className="text-lg text-gray-600 mt-1">{userProfile.title}</p>
                    <div className="flex items-center mt-2 text-sm text-gray-500">
                      <BriefcaseIcon className="h-4 w-4 mr-1" />
                      {userProfile.department}
                      <span className="mx-2">•</span>
                      <IdentificationIcon className="h-4 w-4 mr-1" />
                      {userProfile.employeeId}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {!isEditing ? (
                      <button
                        onClick={handleEdit}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <PencilIcon className="h-4 w-4 mr-2" />
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleCancel}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <XMarkIcon className="h-4 w-4 mr-2" />
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving}
                          className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                        >
                          {saving ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <CheckIcon className="h-4 w-4 mr-2" />
                          )}
                          {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bio */}
                <div className="mt-4">
                  {isEditing ? (
                    <textarea
                      value={editForm.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      rows={3}
                      className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Tell us about yourself..."
                    />
                  ) : (
                    <p className="text-gray-700">{userProfile.bio}</p>
                  )}
                </div>

                {/* Contact Info */}
                <div className="mt-4 flex flex-wrap gap-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    {userProfile.email}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <PhoneIcon className="h-4 w-4 mr-2" />
                    {userProfile.phone}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <MapPinIcon className="h-4 w-4 mr-2" />
                    {userProfile.location}
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Joined {new Date(userProfile.dateJoined).toLocaleDateString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserGroupIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Patients Served</p>
                <p className="text-2xl font-semibold text-gray-900">{userProfile.stats.patientsServed.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <ChartBarIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Procedures</p>
                <p className="text-2xl font-semibold text-gray-900">{userProfile.stats.proceduresPerformed.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <StarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Satisfaction</p>
                <p className="text-2xl font-semibold text-gray-900">{userProfile.stats.patientSatisfaction}/5.0</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <AcademicCapIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Experience</p>
                <p className="text-2xl font-semibold text-gray-900">{userProfile.stats.yearsExperience} years</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: UserCircleIcon },
              { id: 'professional', name: 'Professional Info', icon: BriefcaseIcon },
              { id: 'achievements', name: 'Achievements', icon: TrophyIcon },
              { id: 'activity', name: 'Recent Activity', icon: ClockIcon }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Contact Information */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{userProfile.email}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{userProfile.phone}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.location}
                          onChange={(e) => handleInputChange('location', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{userProfile.location}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
                      {isEditing ? (
                        <select
                          value={editForm.timezone}
                          onChange={(e) => handleInputChange('timezone', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Eastern Time (ET)">Eastern Time (ET)</option>
                          <option value="Central Time (CT)">Central Time (CT)</option>
                          <option value="Mountain Time (MT)">Mountain Time (MT)</option>
                          <option value="Pacific Time (PT)">Pacific Time (PT)</option>
                        </select>
                      ) : (
                        <p className="text-gray-900">{userProfile.timezone}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Working Hours */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Working Hours</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Time</label>
                      {isEditing ? (
                        <input
                          type="time"
                          value={editForm.workingHours?.start}
                          onChange={(e) => handleInputChange('start', e.target.value, 'workingHours')}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{userProfile.workingHours.start}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Time</label>
                      {isEditing ? (
                        <input
                          type="time"
                          value={editForm.workingHours?.end}
                          onChange={(e) => handleInputChange('end', e.target.value, 'workingHours')}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        />
                      ) : (
                        <p className="text-gray-900">{userProfile.workingHours.end}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'professional' && (
              <div className="space-y-6">
                {/* Education */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Education</h3>
                  <div className="space-y-4">
                    {userProfile.professionalInfo.education.map((edu, index) => (
                      <div key={index} className="border-l-4 border-blue-500 pl-4">
                        <h4 className="font-semibold text-gray-900">{edu.degree} in {edu.field}</h4>
                        <p className="text-gray-600">{edu.institution}</p>
                        <p className="text-sm text-gray-500">Graduated {edu.year}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Certifications */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Certifications</h3>
                  <div className="space-y-4">
                    {userProfile.professionalInfo.certifications.map((cert, index) => (
                      <div key={index} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg">
                        <div>
                          <h4 className="font-semibold text-gray-900">{cert.name}</h4>
                          <p className="text-gray-600">{cert.issuer}</p>
                          <p className="text-sm text-gray-500">Issued {cert.year} • Expires {cert.expires}</p>
                        </div>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'achievements' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Achievements & Awards</h3>
                <div className="space-y-4">
                  {userProfile.achievements.map((achievement) => (
                    <div key={achievement.id} className="flex items-start p-4 border border-gray-200 rounded-lg">
                      <div className="flex-shrink-0 mr-4">
                        {getAchievementIcon(achievement.icon)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-semibold text-gray-900">{achievement.title}</h4>
                          <span className="text-sm text-gray-500">
                            {new Date(achievement.date).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-gray-600 mt-1">{achievement.description}</p>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-2">
                          {achievement.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Recent Activity</h3>
                <div className="space-y-4">
                  {userProfile.recentActivity.map((activity) => (
                    <div key={activity.id} className="flex items-start p-4 hover:bg-gray-50 rounded-lg">
                      <div className="flex-shrink-0 mr-4">
                        <div className="p-2 bg-gray-100 rounded-full">
                          {getActivityIcon(activity.type)}
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-900">{activity.title}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {activity.date.toLocaleDateString()} at {activity.date.toLocaleTimeString('en-US', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                      <button className="p-1 text-gray-400 hover:text-gray-600">
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <Link
                  to="/profile/settings"
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <Cog6ToothIcon className="h-4 w-4 mr-3" />
                  Account Settings
                </Link>
                <Link
                  to="/profile/privacy"
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <ShieldCheckIcon className="h-4 w-4 mr-3" />
                  Privacy Settings
                </Link>
                <Link
                  to="/profile/notifications"
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <BellIcon className="h-4 w-4 mr-3" />
                  Notification Preferences
                </Link>
                <Link
                  to="/profile/export"
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                >
                  <ShareIcon className="h-4 w-4 mr-3" />
                  Export Data
                </Link>
              </div>
            </div>

            {/* Social Links */}
            {userProfile.socialLinks && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Links</h3>
                <div className="space-y-3">
                  {Object.entries(userProfile.socialLinks).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                    >
                      <GlobeAltIcon className="h-4 w-4 mr-3" />
                      <span className="capitalize">{platform}</span>
                      <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-auto" />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Summary */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity Summary</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Training Completed</span>
                  <span className="text-sm font-medium text-gray-900">{userProfile.stats.completedTraining}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Upcoming Events</span>
                  <span className="text-sm font-medium text-gray-900">{userProfile.stats.upcomingEvents}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Tasks Completed</span>
                  <span className="text-sm font-medium text-gray-900">{userProfile.stats.tasksCompleted}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Documents Shared</span>
                  <span className="text-sm font-medium text-gray-900">{userProfile.stats.documentsShared}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default UserProfile;
